import React, { lazy } from 'react'
import BasicLayout from '@/components/layout/BasicLayout'
import { lazyWithListDefaults } from '@/components/common/listDefaults/ListDefaultQueryGate'
import NothingLayout from '@/components/layout/NothingLayout'
import routerConstants from '@/constants/routerConstants'
import { ALL_CHUCNANG_PERMISSION_CODES } from '@/constants/permissionConstants'
import { appendUiTestRoutes } from '@/modules/ui-test'
import { appendTestModuleChiTietRoutes } from '@/modules/test-module-chitiet'
import { lazyEditIdListRedirect, lazyDetailIdListRedirect } from '@/components/admin/_shared/EditIdListRedirect'
import { enableVanHanh } from '@/config'
import { OFFLINE_MAU_IN_PATH } from '@/apis/mauInOfflineStore'

/** Route config (giống sanxuat) */
export interface RouteConfig {
  path: string
  name: string
  exact?: boolean
  layout: React.ComponentType<{ children: React.ReactNode }>
  component: React.LazyExoticComponent<React.ComponentType>
  title?: string
  /** `kho` → tab title «KHO - …» + favicon kho (mặc định «BH - …»). */
  module?: 'kho'
}

/** Các route công khai (không cần đăng nhập) */
const routePublic: RouteConfig[] = [
  {
    path: '/login',
    name: 'Login',
    exact: true,
    layout: NothingLayout,
    component: lazy(() => import('@/components/login/Login')),
    title: 'Đăng nhập',
  },
  {
    path: '/developer/contact',
    name: 'ITContact',
    exact: true,
    layout: NothingLayout,
    component: lazy(() => import('@/components/page/ITContact')),
    title: 'Liên hệ IT',
  },
  {
    path: '/developer/privacy-policy',
    name: 'PrivacyPolicy',
    exact: true,
    layout: NothingLayout,
    component: lazy(() => import('@/components/page/PrivacyPolicy')),
    title: 'Chính sách bảo mật',
  },

  {
    path: '/unauthorized',
    name: 'Unauthorized',
    exact: true,
    layout: BasicLayout,
    component: lazy(() => import('@/components/404/Unauthorized')),
    title: 'Không có quyền',
  },
  {
    path: routerConstants.public.containerShipments,
    name: 'PublicContainerShipments',
    exact: true,
    layout: NothingLayout,
    component: lazy(() => import('@/components/public/PublicContainerShipmentsPage')),
    title: 'Lô container',
  },
  ...(process.env.NODE_ENV === 'development'
    ? [
        {
          path: OFFLINE_MAU_IN_PATH,
          name: 'OfflineMauIn',
          exact: true,
          layout: NothingLayout,
          component: lazy(() => import('@/components/admin/mauIn/MauInOfflinePage')),
          title: 'Mẫu in (offline)',
        },
      ]
    : []),
]

export interface GenerateRoutesOptions {
  /** Bỏ qua kiểm tra quyền — dùng để lấy toàn bộ route tồn tại (phân biệt 403 với 404). */
  grantAll?: boolean
}

/** Route ứng dụng theo mã chức năng (đồng bộ cách làm ở src/router/index.tsx). */
export const generateRoutes = (chucNangAll: string[], options: GenerateRoutesOptions = {}): RouteConfig[] => {
  const { grantAll = false } = options
  const hasChucNangByCode = (chucNangCode: string): boolean =>
    grantAll || chucNangAll.includes(chucNangCode)

  const hasAnyChucNangByCode = (codes: string[]): boolean => codes.some(hasChucNangByCode)

  const C = ALL_CHUCNANG_PERMISSION_CODES
  const routes: RouteConfig[] = [...routePublic]

  if (hasChucNangByCode(C.WEB_BH_DASHBOARD_LIST)) {
    routes.push(
      {
        path: '/',
        name: 'Home',
        exact: true,
        layout: BasicLayout,
        component: lazy(() => import('@/components/home/Home')),
        title: 'Trang chủ',
      },
      {
        path: routerConstants.dashboard,
        name: 'Dashboard',
        exact: true,
        layout: BasicLayout,
        component: lazy(() => import('@/components/home/Home')),
        title: 'Trang chủ',
      },
    )
  }

  if (hasChucNangByCode(C.WEB_BH_PRODUCT_LIST)) {
    routes.push({
      path: routerConstants.product.list,
      name: 'ProductList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/product/ProductList')),
      title: 'Hàng hóa',
    })
    routes.push({
      path: routerConstants.product.detail,
      name: 'ProductDetail',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/product/ProductDetail')),
      title: 'Chi tiết hàng hóa',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_ACCOUNT_LIST)) {
    routes.push({
      path: routerConstants.account,
      name: 'Account',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/account/AccountPage')),
      title: 'Tài khoản',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_SETTINGS_LIST)) {
    routes.push({
      path: routerConstants.settings,
      name: 'Settings',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/settings/SettingsPage')),
      title: 'Cài đặt',
    })
  }

  if (hasAnyChucNangByCode([C.WEB_BH_MAU_IN_LIST, C.WEB_BH_SETTINGS_LIST])) {
    routes.push({
      path: routerConstants.settingsMauIn,
      name: 'SettingsMauIn',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/mauIn/MauInPage')),
      title: 'Mẫu in',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_NHOM_HANG_LIST)) {
    routes.push({
      path: routerConstants.nhomHang.list,
      name: 'NhomHangList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/nhomHang/NhomHangPage')),
      title: 'Nhóm hàng',
    })
    routes.push({
      path: routerConstants.nhomHang.tree,
      name: 'NhomHangTreeList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/nhomHang/NhomHangTreeRedirect')),
      title: 'Nhóm hàng — dạng cây',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_NHOM_KHACH_HANG_LIST)) {
    routes.push({
      path: routerConstants.nhomKhachHang.list,
      name: 'NhomKhachHangList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/nhomKhachHang/NhomKhachHangList')),
      title: 'Nhóm khách hàng',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_KHACH_HANG_LIST)) {
    routes.push({
      path: routerConstants.khachHang.list,
      name: 'KhachHangList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/khachHang/KhachHangList')),
      title: 'Khách hàng',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_NHA_CUNG_CAP_LIST)) {
    routes.push({
      path: routerConstants.nhaCungCap.list,
      name: 'NhaCungCapList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/nhaCungCap/NhaCungCapList')),
      title: 'Nhà cung cấp',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_BANG_GIA_LIST)) {
    routes.push({
      path: routerConstants.bangGia.list,
      name: 'BangGiaList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('../components/admin/bangGia/BangGiaList')),
      title: 'Bảng giá',
    })
    routes.push({
      path: routerConstants.bangGia.setup,
      name: 'BangGiaSetup',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('../components/admin/bangGia/BangGiaPriceSetup')),
      title: 'Thiết lập giá',
    })
    routes.push({
      path: routerConstants.bangGiaHangHoa.list,
      name: 'BangGiaHangHoaList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('../components/admin/bangGiaHangHoa/BangGiaHangHoaList')),
      title: 'Bảng giá hàng hóa',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_PHIEU_NHAP_KHO_LIST)) {
    routes.push({
      path: routerConstants.phieuNhapKho.list,
      name: 'PhieuNhapKhoList',
      exact: true,
      layout: BasicLayout,
      component: lazyWithListDefaults(
        () => import('@/components/admin/phieuNhapKho/PhieuNhapKhoList'),
        {
          time: { from: 'tuNgay', to: 'denNgay' },
          chiNhanhKey: 'chiNhanhId',
          sort: { field: 'ngayChungTu', dir: 'desc' },
        },
      ),
      title: 'Phiếu nhập kho',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_PHIEU_NHAP_KHO_EDIT)) {
    routes.push({
      path: routerConstants.phieuNhapKho.edit,
      name: 'PhieuNhapKhoEdit',
      exact: false,
      layout: BasicLayout,
      component: lazyEditIdListRedirect(routerConstants.phieuNhapKho.list),
      title: 'Sửa phiếu nhập kho',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_PHIEU_XUAT_KHO_LIST)) {
    routes.push({
      path: routerConstants.phieuXuatKho.list,
      name: 'PhieuXuatKhoList',
      exact: true,
      layout: BasicLayout,
      component: lazyWithListDefaults(
        () => import('@/components/admin/phieuXuatKho/PhieuXuatKhoList'),
        {
          time: { from: 'tuNgay', to: 'denNgay' },
          chiNhanhKey: 'chiNhanhId',
          sort: { field: 'ngayChungTu', dir: 'desc' },
        },
      ),
      title: 'Phiếu xuất kho',
    })
    routes.push({
      path: routerConstants.xuatHuy.list,
      name: 'XuatHuyList',
      exact: true,
      layout: BasicLayout,
      component: lazyWithListDefaults(
        () => import('@/components/admin/xuatHuy/XuatHuyList'),
        {
          time: { from: 'tuNgay', to: 'denNgay' },
          chiNhanhKey: 'chiNhanhId',
          sort: { field: 'ngayChungTu', dir: 'desc' },
        },
      ),
      title: 'Xuất hủy',
    })
    routes.push({
      path: routerConstants.xuatTraNcc.list,
      name: 'XuatTraNccList',
      exact: true,
      layout: BasicLayout,
      component: lazyWithListDefaults(
        () => import('@/components/admin/xuatTraNcc/XuatTraNccList'),
        {
          time: { from: 'tuNgay', to: 'denNgay' },
          chiNhanhKey: 'chiNhanhId',
          sort: { field: 'ngayChungTu', dir: 'desc' },
        },
      ),
      title: 'Xuất trả NCC',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_PHIEU_XUAT_KHO_EDIT)) {
    routes.push({
      path: routerConstants.phieuXuatKho.edit,
      name: 'PhieuXuatKhoEdit',
      exact: false,
      layout: BasicLayout,
      component: lazyEditIdListRedirect(routerConstants.phieuXuatKho.list),
      title: 'Sửa phiếu xuất kho',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_PHIEU_NHAN_HANG_LIST)) {
    routes.push({
      path: routerConstants.phieuNhanHang.list,
      name: 'PhieuNhanHangList',
      exact: true,
      layout: BasicLayout,
      component: lazyWithListDefaults(
        () => import('@/components/admin/phieuNhanHang/PhieuNhanHangList'),
        {
          time: { from: 'tuNgay', to: 'denNgay' },
          chiNhanhKey: 'chiNhanhId',
          sort: { field: 'ngayChungTu', dir: 'desc' },
        },
      ),
      title: 'Phiếu nhận hàng',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_PHIEU_NHAN_HANG_EDIT)) {
    routes.push({
      path: routerConstants.phieuNhanHang.edit,
      name: 'PhieuNhanHangEdit',
      exact: false,
      layout: BasicLayout,
      component: lazyEditIdListRedirect(routerConstants.phieuNhanHang.list),
      title: 'Sửa phiếu nhận hàng',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_DAT_HANG_LIST)) {
    routes.push({
      path: routerConstants.datHang.list,
      name: 'DatHangList',
      exact: true,
      layout: BasicLayout,
      component: lazyWithListDefaults(
        () => import('@/components/admin/datHang/DatHangList'),
        {
          time: { from: 'thoiGianTu', to: 'thoiGianDen' },
          chiNhanhKey: 'chiNhanhIds',
          sort: { field: 'thoiGian', dir: 'desc' },
        },
      ),
      title: 'Đặt hàng',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_DAT_HANG_CHI_TIET_LIST)) {
    routes.push({
      path: routerConstants.datHang.chiTietHang,
      name: 'DatHangChiTietHangList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/datHang/DatHangChiTietHangList')),
      title: 'Đặt hàng — chi tiết hàng',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_DAT_HANG_EDIT)) {
    routes.push({
      path: routerConstants.datHang.edit,
      name: 'DatHangEdit',
      exact: false,
      layout: BasicLayout,
      component: lazyEditIdListRedirect(routerConstants.datHang.list),
      title: 'Sửa đặt hàng',
    })
  }

  // Redirect URL cũ /admin/don-hang/* → /admin/dat-hang/*
  routes.push({
    path: '/admin/don-hang/list',
    name: 'DonHangListRedirect',
    exact: true,
    layout: BasicLayout,
    component: lazy(() => import('@/components/admin/datHang/DatHangPathRedirect')),
    title: 'Đặt hàng',
  })
  routes.push({
    path: '/admin/don-hang/edit/:id',
    name: 'DonHangEditRedirect',
    exact: false,
    layout: BasicLayout,
    component: lazy(() => import('@/components/admin/datHang/DatHangPathRedirect')),
    title: 'Sửa đặt hàng',
  })

  if (hasChucNangByCode(C.WEB_BH_HOA_DON_LIST)) {
    routes.push({
      path: routerConstants.hoaDon.list,
      name: 'HoaDonList',
      exact: true,
      layout: BasicLayout,
      component: lazyWithListDefaults(
        () => import('@/components/admin/hoaDon/HoaDonList'),
        {
          time: { from: 'thoiGianTu', to: 'thoiGianDen' },
          chiNhanhKey: 'chiNhanhIds',
          sort: { field: 'thoiGian', dir: 'desc' },
        },
      ),
      title: 'Hóa đơn',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_HOA_DON_CHI_TIET_LIST)) {
    routes.push({
      path: routerConstants.hoaDon.chiTietHang,
      name: 'HoaDonChiTietHangList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/hoaDon/chiTietHang/HoaDonChiTietHangList')),
      title: 'Hóa đơn — chi tiết hàng',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_HOA_DON_LIST)) {
    routes.push({
      path: routerConstants.banHang,
      name: 'BanHangPos',
      exact: false,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/hoaDon/pos/HoaDonPosPage')),
      title: 'Bán hàng',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_HOA_DON_EDIT)) {
    routes.push({
      path: routerConstants.hoaDon.edit,
      name: 'HoaDonEdit',
      exact: false,
      layout: BasicLayout,
      component: lazyEditIdListRedirect(routerConstants.hoaDon.list),
      title: 'Sửa hóa đơn',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_DON_VI_TINH_LIST)) {
    routes.push({
      path: routerConstants.donViTinh.list,
      name: 'DonViTinhList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/donViTinh/DonViTinhList')),
      title: 'Đơn vị tính',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_THUONG_HIEU_LIST)) {
    routes.push({
      path: routerConstants.thuongHieu.list,
      name: 'ThuongHieuList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/thuongHieu/ThuongHieuList')),
      title: 'Thương hiệu',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_PHAN_LOAI_SAN_PHAM_LIST)) {
    routes.push({
      path: routerConstants.phanLoaiSanPham.list,
      name: 'PhanLoaiSanPhamList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/phanLoaiSanPham/PhanLoaiSanPhamList')),
      title: 'Phân loại sản phẩm',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_THONG_TIN_VAT_HANG_HOA_LIST)) {
    routes.push({
      path: routerConstants.thongTinVatHangHoa.list,
      name: 'ThongTinVatHangHoaList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/thongTinVatHangHoa/ThongTinVatHangHoaList')),
      title: 'Thông tin VAT hàng hóa',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_THUOC_TINH_LIST)) {
    routes.push({
      path: routerConstants.thuocTinh.list,
      name: 'ThuocTinhList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/thuocTinh/ThuocTinhList')),
      title: 'Thuộc tính hàng hóa',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_VI_TRI_LIST)) {
    routes.push({
      path: routerConstants.viTri.list,
      name: 'ViTriList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/viTri/ViTriList')),
      title: 'Vị trí kho',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_DIA_CHI_LAY_HANG_LIST)) {
    routes.push({
      path: routerConstants.diaChiLayHang.list,
      name: 'DiaChiLayHangList',
      exact: true,
      layout: BasicLayout,
      component: lazy(
        () => import('@/components/admin/diaChiLayHang/DiaChiLayHangList'),
      ),
      title: 'Địa chỉ lấy hàng',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_TRA_HANG_LIST)) {
    routes.push({
      path: routerConstants.traHang.list,
      name: 'TraHangList',
      exact: true,
      layout: BasicLayout,
      component: lazyWithListDefaults(
        () => import('@/components/admin/traHang/TraHangList'),
        {
          time: { from: 'tuNgay', to: 'denNgay' },
          chiNhanhKey: 'chiNhanhIds',
          sort: { field: 'thoiGian', dir: 'desc' },
        },
      ),
      title: 'Trả hàng',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_TRA_HANG_EDIT)) {
    routes.push({
      path: routerConstants.traHang.edit,
      name: 'TraHangEdit',
      exact: false,
      layout: BasicLayout,
      component: lazyEditIdListRedirect(routerConstants.traHang.list),
      title: 'Sửa phiếu trả hàng',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_YEU_CAU_SUA_CHUA_LIST)) {
    routes.push({
      path: routerConstants.yeuCauSuaChua.list,
      name: 'YeuCauSuaChuaList',
      exact: true,
      layout: BasicLayout,
      component: lazyWithListDefaults(
        () => import('@/components/admin/yeuCauSuaChua/YeuCauSuaChuaList'),
        { time: { from: 'tuNgay', to: 'denNgay' }, chiNhanhKey: 'chiNhanhId' },
      ),
      title: 'Yêu cầu sửa chữa',
    })
    routes.push({
      path: routerConstants.yeuCauSuaChua.chiTiet,
      name: 'YeuCauSuaChuaChiTiet',
      exact: true,
      layout: BasicLayout,
      component: lazyWithListDefaults(
        () => import('@/components/admin/yeuCauSuaChua/YeuCauSuaChuaList'),
        { time: { from: 'tuNgay', to: 'denNgay' }, chiNhanhKey: 'chiNhanhId' },
      ),
      title: 'Chi tiết yêu cầu sửa chữa',
    })
    routes.push({
      path: routerConstants.yeuCauSuaChua.baoCao,
      name: 'YeuCauSuaChuaBaoCao',
      exact: true,
      layout: BasicLayout,
      component: lazyWithListDefaults(
        () => import('@/components/admin/yeuCauSuaChua/YeuCauSuaChuaList'),
        { time: { from: 'tuNgay', to: 'denNgay' }, chiNhanhKey: 'chiNhanhId' },
      ),
      title: 'Báo cáo yêu cầu sửa chữa',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_YEU_CAU_SUA_CHUA_EDIT)) {
    routes.push({
      path: routerConstants.yeuCauSuaChua.edit,
      name: 'YeuCauSuaChuaEdit',
      exact: false,
      layout: BasicLayout,
      component: lazyEditIdListRedirect(routerConstants.yeuCauSuaChua.list),
      title: 'Sửa yêu cầu sửa chữa',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_YEU_CAU_SUA_CHUA_DETAIL)) {
    routes.push({
      path: routerConstants.yeuCauSuaChua.detail,
      name: 'YeuCauSuaChuaDetail',
      exact: false,
      layout: BasicLayout,
      component: lazyEditIdListRedirect(routerConstants.yeuCauSuaChua.list),
      title: 'Chi tiết yêu cầu sửa chữa',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_KIEM_KE_LIST)) {
    routes.push({
      path: routerConstants.kiemKe.list,
      name: 'KiemKeList',
      exact: true,
      layout: BasicLayout,
      component: lazyWithListDefaults(
        () => import('@/components/admin/kiemKe/KiemKeList'),
        {
          time: { from: 'tuNgay', to: 'denNgay' },
          chiNhanhKey: 'chiNhanhId',
          sort: { field: 'thoiGian', dir: 'desc', fieldKey: 'sortField', dirKey: 'sortOrder' },
        },
      ),
      title: 'Kiểm kê',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_KIEM_KE_EDIT)) {
    routes.push({
      path: routerConstants.kiemKe.edit,
      name: 'KiemKeEdit',
      exact: false,
      layout: BasicLayout,
      component: lazyEditIdListRedirect(routerConstants.kiemKe.list),
      title: 'Sửa phiếu kiểm kê',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_KIEM_KE_DETAIL)) {
    routes.push({
      path: routerConstants.kiemKe.detail,
      name: 'KiemKeDetail',
      exact: false,
      layout: BasicLayout,
      component: lazyDetailIdListRedirect(routerConstants.kiemKe.list),
      title: 'Chi tiết phiếu kiểm kê',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_DANH_MUC_THU_KHAC_LIST)) {
    routes.push({
      path: routerConstants.danhMucThuKhac.list,
      name: 'DanhMucThuKhacList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/danhMucThuKhac/DanhMucThuKhacList')),
      title: 'Danh mục thu khác',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_AUDIT_LIST)) {
    routes.push({
      path: routerConstants.audit.list,
      name: 'AuditList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/auditLog/AuditList')),
      title: 'Nhật ký thao tác',
    })
  }

  routes.push({
    path: routerConstants.logLoiHeThong.list,
    name: 'LogLoiHeThongList',
    exact: true,
    layout: BasicLayout,
    component: lazy(() => import('@/components/admin/logLoiHeThong/LogLoiHeThongList')),
    title: 'Log lỗi hệ thống',
  })

  if (hasChucNangByCode(C.WEB_BH_USER_LIST)) {
    routes.push({
      path: routerConstants.user.list,
      name: 'UserList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/user/UserList')),
      title: 'Người dùng',
    })
  }

  if (hasAnyChucNangByCode([C.WEB_CHAT_LIST, C.WEB_CHAT_CHAT])) {
    routes.push({
      path: routerConstants.chat,
      name: 'Chat',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/chat/ChatPage')),
      title: 'Tin nhắn',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_CONG_NO_KHACH_HANG_LIST)) {
    routes.push(
      {
        path: routerConstants.congNo.khachHang,
        name: 'CongNoKhachHangList',
        exact: true,
        layout: BasicLayout,
        component: lazy(() => import('@/components/admin/congNo/CongNoList')),
        title: 'Công nợ khách hàng',
      },
      {
        path: routerConstants.congNo.detail,
        name: 'CongNoKhachHangDetail',
        exact: false,
        layout: BasicLayout,
        component: lazy(() => import('@/components/admin/congNo/CongNoDetail')),
        title: 'Chi tiết công nợ khách hàng',
      },
      {
        path: routerConstants.congNo.legacyList,
        name: 'CongNoLegacyRedirect',
        exact: true,
        layout: BasicLayout,
        component: lazy(() => import('@/components/admin/congNo/CongNoLegacyRedirect')),
        title: 'Công nợ',
      },
    )
  }
  if (hasChucNangByCode(C.WEB_BH_CONG_NO_THEO_DON_LIST)) {
    routes.push({
      path: routerConstants.congNo.theoDon,
      name: 'CongNoTheoDonList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/congNo/CongNoList')),
      title: 'Công nợ theo đơn',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_CONG_NO_THEO_DON_CHI_TIET_LIST)) {
    routes.push({
      path: routerConstants.congNo.theoDonChiTiet,
      name: 'CongNoTheoDonChiTietList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/congNo/CongNoTheoDonChiTietList')),
      title: 'Công nợ theo đơn chi tiết',
    })
  }
  if (
    hasAnyChucNangByCode([C.WEB_BH_CONG_NO_THANH_TOAN_LIST, C.WEB_BH_CONG_NO_HOAN_TIEN_LIST])
  ) {
    routes.push({
      path: routerConstants.congNo.thanhToan,
      name: 'CongNoThanhToanList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/congNo/CongNoList')),
      title: 'Thanh toán',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_CONG_NO_HOAN_TIEN_LIST)) {
    routes.push({
      path: routerConstants.congNo.hoanTien,
      name: 'CongNoHoanTienList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/congNo/CongNoList')),
      title: 'Hoàn tiền',
    })
  }

  // === Module Sổ Quỹ (Story 1.7) — top-level /so-quy/* routes ===
  // 3 routes stub: 1.9 (PhieuThuList), 2.5 (PhieuChiList), 4.3 (BaoCaoPage) sẽ thay thế.
  // Detail route (/so-quy/phieu-thu/:id) defer sang Story 1.10.
  if (hasChucNangByCode(C.WEB_BH_SO_QUY_THU_VIEW)) {
    routes.push({
      path: routerConstants.soQuy.phieuThu,
      name: 'SoQuyPhieuThuList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/soQuy/PhieuThuList')),
      title: 'Phiếu thu',
    })
  }
  // Story 1.10 — Detail page (/so-quy/phieu-thu/:id). exact:false vì có param :id
  // (mirror WarehouseDetail/ChuyenHangDetail). Gate cùng permission list (WEB_BH_SO_QUY_THU_VIEW).
  if (hasChucNangByCode(C.WEB_BH_SO_QUY_THU_VIEW)) {
    routes.push({
      path: routerConstants.soQuy.phieuThuDetail,
      name: 'SoQuyPhieuThuDetail',
      exact: false,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/soQuy/PhieuThuDetail')),
      title: 'Chi tiết phiếu thu',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_SO_QUY_CHI_VIEW)) {
    routes.push({
      path: routerConstants.soQuy.phieuChi,
      name: 'SoQuyPhieuChiList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/soQuy/PhieuChiList')),
      title: 'Phiếu chi',
    })
  }
  // Story 2.6 — Detail page (/so-quy/phieu-chi/:id). exact:false vì có param :id
  // (mirror PhieuThuDetail 1.10 + WarehouseDetail). Gate cùng permission list (WEB_BH_SO_QUY_CHI_VIEW).
  if (hasChucNangByCode(C.WEB_BH_SO_QUY_CHI_VIEW)) {
    routes.push({
      path: routerConstants.soQuy.phieuChiDetail,
      name: 'SoQuyPhieuChiDetail',
      exact: false,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/soQuy/PhieuChiDetail')),
      title: 'Chi tiết phiếu chi',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_SO_QUY_BAO_CAO_VIEW)) {
    routes.push({
      path: routerConstants.soQuy.baoCao,
      name: 'SoQuyBaoCaoPage',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/soQuy/BaoCaoPage')),
      title: 'Báo cáo sổ quỹ',
    })
  }
  // Story 3.3 — Trang hợp nhất Sổ quỹ tổng hợp (FR-10). Gate OR logic
  // `WEB_BH_SO_QUY_THU_VIEW ∨ CHI_VIEW` (BE 3.1 §3.1 SSO pathKey `so-quy`). TopNav
  // menu đã có sẵn sub-item 'Sổ quỹ' từ 1.7 (TopNav dropdown — route register
  // defer Epic 3, 3.3 thực hiện).
  if (hasAnyChucNangByCode([C.WEB_BH_SO_QUY_THU_VIEW, C.WEB_BH_SO_QUY_CHI_VIEW])) {
    routes.push({
      path: routerConstants.soQuy.soQuy,
      name: 'SoQuyUnifiedList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/soQuy/SoQuyUnifiedList')),
      title: 'Sổ quỹ tổng hợp',
    })
  }

  // Epic 5 — Tài khoản quỹ admin (FR-14). Super admin only.
  if (hasChucNangByCode(C.WEB_BH_TAI_KHOAN_QUY_MANAGE)) {
    routes.push({
      path: routerConstants.soQuy.taiKhoanQuy,
      name: 'TaiKhoanQuyList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/soQuy/TaiKhoanQuyList')),
      title: 'Tài khoản quỹ',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_LOAI_THU_CHI_MANAGE)) {
    routes.push({
      path: routerConstants.soQuy.loaiThuChi,
      name: 'LoaiThuChiList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/soQuy/LoaiThuChiList')),
      title: 'Loại thu chi',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_CHUYEN_HANG_LIST)) {
    routes.push({
      path: routerConstants.chuyenHang.list,
      name: 'ChuyenHangList',
      exact: true,
      layout: BasicLayout,
      component: lazyWithListDefaults(
        () => import('@/components/admin/chuyenHang/ChuyenHangList'),
        {
          time: { from: 'tuNgay', to: 'denNgay' },
          chiNhanhKey: 'chiNhanhId',
          sort: { field: 'ngayChuyen', dir: 'desc' },
        },
      ),
      title: 'Chuyển hàng',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_CHUYEN_HANG_EDIT)) {
    routes.push({
      path: routerConstants.chuyenHang.edit,
      name: 'ChuyenHangEdit',
      exact: false,
      layout: BasicLayout,
      component: lazyEditIdListRedirect(routerConstants.chuyenHang.list),
      title: 'Sửa phiếu chuyển',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_MAU_IN_LIST)) {
    routes.push({
      path: routerConstants.mauIn.hoaDon,
      name: 'MauInHoaDon',
      exact: true,
      layout: BasicLayout,
      component: lazyEditIdListRedirect(`${routerConstants.settingsMauIn}?man=hoa-don`),
      title: 'Mẫu in hóa đơn',
    })
  }

  // Module YCX (Yêu cầu xuất hóa đơn) — gộp 2 queue (queue-kt + queue-nvbh) cũ
  // thành 1 trang tổng hợp `/danh-sach`, gate `hasAnyChucNangByCode` để cả NVBH
  // lẫn KT xem được. Filter trạng thái + date range + hóa đơn gốc + reject ở sidebar.
  if (hasAnyChucNangByCode([C.WEB_BH_YCX_LIST, C.WEB_BH_YCX_LIST_KT])) {
    routes.push({
      path: routerConstants.yeuCauXuatHoaDon.list,
      name: 'YeuCauXuatHoaDonList',
      exact: true,
      layout: BasicLayout,
      component: lazyWithListDefaults(
        () => import('@/components/admin/yeuCauXuatHoaDon/YeuCauXuatHoaDonList'),
        { time: { from: 'ngayTu', to: 'ngayDen' }, chiNhanhKey: 'chiNhanhIds' },
      ),
      title: 'Yêu cầu xuất hóa đơn',
    })
  }
  if (hasAnyChucNangByCode([C.WEB_BH_YCX_LIST, C.WEB_BH_YCX_LIST_KT, C.WEB_BH_YCX_EDIT])) {
    routes.push({
      path: routerConstants.yeuCauXuatHoaDon.detail,
      name: 'YeuCauXuatHoaDonDetail',
      exact: false,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/yeuCauXuatHoaDon/YeuCauXuatHoaDonDetail')),
      title: 'Chi tiết YCX',
    })
  }
  if (hasChucNangByCode(C.WEB_BH_YCX_TAO)) {
    routes.push({
      path: routerConstants.yeuCauXuatHoaDon.create,
      name: 'YeuCauXuatHoaDonCreate',
      exact: false,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/yeuCauXuatHoaDon/YeuCauXuatHoaDonCreate')),
      title: 'Tạo YCX',
    })
  }

  appendUiTestRoutes(routes)
  appendTestModuleChiTietRoutes(routes)

  routes.push({
    path: routerConstants.uiKit,
    name: 'BaseUiKit',
    exact: true,
    layout: BasicLayout,
    component: lazy(() => import('@/components/admin/uiKit/BaseUiKitPage')),
    title: 'Base UI Kit',
  })

  if (hasChucNangByCode(C.WEB_BH_DIA_CHI_NHAN_HANG_LIST)) {
    routes.push({
      path: routerConstants.diaChiNhanHang.all,
      name: 'DiaChiNhanHangListAll',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/diaChiNhanHang/DiaChiNhanHangList')),
      title: 'Thông tin giao hàng',
    })
    routes.push({
      path: '/admin/dia-chi-nhan-hang/list',
      name: 'DiaChiNhanHangListAllLegacy',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/diaChiNhanHang/DiaChiNhanHangPathRedirect')),
      title: 'Thông tin giao hàng',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_THONG_TIN_XUAT_HOA_DON_LIST)) {
    routes.push({
      path: routerConstants.thongTinXuatHoaDon.all,
      name: 'ThongTinXuatHoaDonListAll',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/thongTinXuatHoaDon/ThongTinXuatHoaDonList')),
      title: 'Thông tin xuất hóa đơn',
    })
    routes.push({
      path: routerConstants.thongTinXuatHoaDon.list,
      name: 'ThongTinXuatHoaDonList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/thongTinXuatHoaDon/ThongTinXuatHoaDonList')),
      title: 'Thông tin xuất hóa đơn',
    })
  }
  // === Module Vận Hành (P4 — gộp kho vào banhang) ===
  // C1: flag REACT_APP_ENABLE_VANHANH (default off) — off thì không đăng ký route
  // van-hanh/thong-bao (deep-link → 404 nội bộ), bất kể user có đủ permission.
  if (enableVanHanh && hasChucNangByCode(C.WEB_KHO_DON_TAT_CA)) {
    routes.push({
      path: routerConstants.vanHanh.list,
      name: 'VanHanhDonHangList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/kho/donHang/VanHanhDonHangList')),
      title: 'Tất cả đơn hàng',
      module: 'kho',
    })
  }
  if (enableVanHanh && hasAnyChucNangByCode([
    C.WEB_KHO_DON_TAT_CA,
    C.WEB_KHO_GAN_NHAT,
    C.WEB_KHO_GAN_DONG,
    C.WEB_KHO_GAN_GIAO,
  ])) {
    routes.push({
      path: routerConstants.vanHanh.detail,
      name: 'VanHanhDonHangDetail',
      exact: false,
      layout: BasicLayout,
      component: lazy(() => import('@/components/kho/donHang/VanHanhDonHangDetail')),
      title: 'Chi tiết đơn hàng',
      module: 'kho',
    })
  }
  if (enableVanHanh && hasChucNangByCode(C.WEB_KHO_NHAT_XEM)) {
    routes.push({
      path: routerConstants.vanHanh.xepNhat,
      name: 'VanHanhXepNhatList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/kho/xepNhat/XepNhatList')),
      title: 'Xếp nhặt',
      module: 'kho',
    })
  }
  if (enableVanHanh && hasChucNangByCode(C.WEB_KHO_KIEM_XEM)) {
    routes.push({
      path: routerConstants.vanHanh.kiemTra,
      name: 'VanHanhKiemTraList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/kho/kiemTra/KiemTraList')),
      title: 'Kiểm tra đơn',
      module: 'kho',
    })
  }
  if (enableVanHanh && hasChucNangByCode(C.WEB_KHO_DONG_XEM)) {
    routes.push({
      path: routerConstants.vanHanh.dongGoi,
      name: 'VanHanhDongGoiList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/kho/dongGoi/DongGoiList')),
      title: 'Đóng gói',
      module: 'kho',
    })
  }
  if (enableVanHanh && hasChucNangByCode(C.WEB_KHO_DIEU_PHOI_XEM)) {
    routes.push({
      path: routerConstants.vanHanh.dieuPhoi,
      name: 'VanHanhDieuPhoiList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/kho/dieuPhoi/DieuPhoiList')),
      title: 'Điều phối giao hàng',
      module: 'kho',
    })
    routes.push({
      path: routerConstants.vanHanh.banDoGiaoHang,
      name: 'BanDoGiaoHang',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/kho/banDoGiaoHang/BanDoGiaoHangPage')),
      title: 'Bản đồ giao hàng',
      module: 'kho',
    })
  }
  if (enableVanHanh && hasChucNangByCode(C.WEB_KHO_GIAO_XEM)) {
    routes.push({
      path: routerConstants.vanHanh.giaoHang,
      name: 'VanHanhGiaoHangList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/kho/giaoHang/GiaoHangList')),
      title: 'Giao hàng',
      module: 'kho',
    })
  }
  if (enableVanHanh && hasChucNangByCode(C.WEB_BH_THONG_BAO)) {
    routes.push({
      path: routerConstants.thongBaoTrungTam,
      name: 'VanHanhThongBaoTrungTam',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/kho/thongBao/ThongBaoTrungTam')),
      title: 'Trung tâm thông báo',
      module: 'kho',
    })
    routes.push({
      path: routerConstants.settingsThongBao,
      name: 'SettingsThongBao',
      exact: true,
      layout: BasicLayout,
      component: lazy(() =>
        import('@/components/settings/SettingsTabRedirect').then((m) => ({
          default: m.ThongBaoSettingsRedirect,
        })),
      ),
      title: 'Cài đặt thông báo',
    })
  }
  // === 2 màn báo cáo kho port y hệt `/admin/report/*` (API native van-hanh/bao-cao) ===
  if (enableVanHanh && hasChucNangByCode(C.WEB_KHO_BAO_CAO)) {
    routes.push({
      path: routerConstants.baoCaoKho.list,
      name: 'BaoCaoChiTietPage',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/kho/baoCao/BaoCaoChiTietPage')),
      title: 'Báo cáo chi tiết đơn hàng',
      module: 'kho',
    })
  }
  if (enableVanHanh && hasAnyChucNangByCode([C.WEB_KHO_BAO_CAO, C.WEB_KHO_BAO_CAO_DON])) {
    routes.push({
      path: routerConstants.baoCaoKho.orderList,
      name: 'BaoCaoDonPage',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/kho/baoCao/BaoCaoDonPage')),
      title: 'Báo cáo theo đơn',
      module: 'kho',
    })
  }

  // URL cũ "Đối tác giao hàng" → Cài đặt › Tích hợp › Giao hàng (cấu hình hãng)
  if (hasChucNangByCode(C.WEB_BH_SETTINGS_LIST)) {
    routes.push({
      path: routerConstants.doiTacGiaoHang.list,
      name: 'DoiTacGiaoHangList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() =>
        import('@/components/settings/SettingsTabRedirect').then((m) => ({ default: m.GiaoHangSettingsRedirect })),
      ),
      title: 'Đối tác giao hàng',
    })
  }

  // === Module Bán online (sàn TMĐT) ===
  if (hasChucNangByCode(C.WEB_BH_DON_HANG_ONLINE_LIST)) {
    routes.push({
      path: routerConstants.donHangOnline.list,
      name: 'DonHangOnlineList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/banOnline/DonHangOnlineList')),
      title: 'Đơn hàng online',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_HANG_HOA_LIEN_KET_LIST)) {
    routes.push({
      path: routerConstants.hangHoaLienKet.list,
      name: 'HangHoaLienKetList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/banOnline/HangHoaLienKetList')),
      title: 'Hàng hóa liên kết',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_DOI_SOAT_ONLINE_LIST)) {
    routes.push({
      path: routerConstants.doiSoatOnline.list,
      name: 'DoiSoatOnlineList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/banOnline/DoiSoatOnlineList')),
      title: 'Đối soát',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_GIAN_HANG_ONLINE_LIST)) {
    routes.push({
      path: routerConstants.gianHangOnline.list,
      name: 'GianHangOnlineList',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/banOnline/GianHangOnlineList')),
      title: 'Gian hàng online',
    })
  }

  if (hasChucNangByCode(C.WEB_BH_THIET_LAP_KENH_LIST)) {
    routes.push({
      path: routerConstants.thietLapKenhOnline,
      name: 'BanOnlineThietLapKenh',
      exact: true,
      layout: BasicLayout,
      component: lazy(() => import('@/components/admin/banOnline/BanOnlineThietLapKenhPage')),
      title: 'Thiết lập kênh',
    })
  }

  if (
    hasAnyChucNangByCode([
      C.WEB_BH_BAO_CAO_TONG_HOP,
      C.WEB_BH_BAO_CAO_DOANH_THU_LN,
      C.WEB_BH_BAO_CAO_TON_KHO,
      C.WEB_BH_BAO_CAO_TOP_SALE,
      C.WEB_BH_BAO_CAO_TOP_MUA,
      C.WEB_BH_BAO_CAO_DA_LUU,
    ])
  ) {
    routes.push(
      {
        path: routerConstants.baoCao.root,
        name: 'BaoCaoIndex',
        exact: true,
        layout: BasicLayout,
        component: lazy(() => import('@/components/admin/baoCao/BaoCaoIndexRedirect')),
        title: 'Báo cáo',
      },
      {
        path: routerConstants.baoCao.doanhThuLnPivot,
        name: 'BaoCaoDoanhThuLnPivot',
        exact: true,
        layout: BasicLayout,
        component: lazy(() => import('@/components/admin/baoCao/BaoCaoPivotRedirect')),
        title: 'Báo cáo',
      },
      {
        path: routerConstants.baoCao.tonKhoPivot,
        name: 'BaoCaoTonKhoPivot',
        exact: true,
        layout: BasicLayout,
        component: lazy(() => import('@/components/admin/baoCao/BaoCaoPivotRedirect')),
        title: 'Báo cáo',
      },
      {
        path: routerConstants.baoCao.daLuu,
        name: 'BaoCaoDaLuu',
        exact: true,
        layout: BasicLayout,
        component: lazy(() => import('@/components/admin/baoCao/luu/BaoCaoDaLuuPage')),
        title: 'Báo cáo đã lưu',
      },
      {
        path: `${routerConstants.baoCao.root}/:loai`,
        name: 'BaoCaoPage',
        exact: true,
        layout: BasicLayout,
        component: lazy(() => import('@/components/admin/baoCao/BaoCaoRoutePage')),
        title: 'Báo cáo',
      },
    )
  }

  return routes
}

export const fallbackRoutes = routePublic
